import { createContext } from 'react';

import type { CurrentUser } from '../api/currentUser';

/**
 * The three answers to "is somebody signed in", and the only three.
 *
 * <h2>Why `unknown` is one of them</h2>
 *
 * A token in storage is not a session — the server decides that, and until `/account/me`
 * answers there is genuinely no answer to give. Collapsing that into "signed out" is the
 * mistake every guard here made in its own way: one bounced a signed-in person to the sign-in
 * screen on every refresh, another showed the sign-in form to somebody who was signed in.
 *
 * <p>So the wait is a state, and a guard renders a loader for it rather than deciding.
 */
export type SessionStatus = 'unknown' | 'signed-in' | 'signed-out';

export interface AuthContextValue {
  /**
   * The one definition of being signed in, shared by every guard.
   *
   * <h2>Why this exists rather than each guard deciding</h2>
   *
   * `ProtectedRoute` asked whether a token was in storage. `GuestRoute` asked whether
   * `/account/me` had answered. Those are different questions, and a browser holding a token
   * the server has rejected answers yes to the first and no to the second — so the dashboard
   * rendered AND `/login` rendered, in the same browser, at the same time. Two guards, two
   * definitions, and the person in front of them saw both.
   *
   * <p>`signed-in` means the server said so. `signed-out` means there is no token, or the
   * server refused the one there is. `unknown` means it is still being asked.
   */
  status: SessionStatus;

  /**
   * The session could not be resolved because the server could not be asked — it is down,
   * unreachable, or answering 5xx.
   *
   * <p>Distinct from `status === 'unknown'` on its own, which also covers the ordinary
   * in-flight moment. This one says the waiting will not end by itself, and it exists so a
   * guard can say that instead of spinning for ever.
   *
   * <p>It is deliberately **not** a fourth `SessionStatus`. Being unable to ask is not a
   * state the session is in; it is a fact about the last attempt, and folding it into the
   * enum would make every guard handle a case that is not about who is signed in.
   */
  sessionUnreachable: boolean;

  /** Asks `/account/me` again. For the control offered when {@link sessionUnreachable}. */
  retrySession: () => void;
  /** Null while loading, and when nobody is signed in. */
  user: CurrentUser | null;
  /**
   * A token is in storage. **This is not "signed in"** — only the server can say that, and
   * `status` is where it says it.
   *
   * <h2>The one thing it may be used for</h2>
   *
   * Turning somebody AWAY from a screen meant for the signed out: `/login`, `/signup`, and
   * the rest of that group. A token is enough to say "you do not belong on the sign-in
   * form", and deciding it needs no server, so nobody waits and no loader appears in front
   * of a redirect that was never in doubt.
   *
   * <h2>What it must never be used for</h2>
   *
   * Letting anybody IN. A token in storage may be expired, revoked, or somebody else's from
   * before this application changed how it stored them. `ProtectedRoute` and every screen
   * behind it read `status`, which waits for `/account/me`. Deciding on this flag would
   * recreate exactly the split the two guards had — one asking storage, the other asking the
   * server — and the browser where both the dashboard and the sign-in form rendered at once.
   *
   * <p>A dead token therefore costs one bounce and no more: the redirect sends them to the
   * dashboard, its guard gets the 401, the session is forgotten, and they arrive back at the
   * sign-in form with storage empty and nothing to bounce again.
   */
  hasStoredSession: boolean;

  /** `status === 'unknown'`: the session is still being established. */
  isLoading: boolean;
  /**
   * Exchanges credentials for a session. Throws an `ApiError` if refused.
   *
   * <p>Two arguments, since 28 September 2026. It took a `remember` flag, which chose
   * between two stores — that was the whole of "Remember me", and both the checkbox and the
   * second store are gone.
   */
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

/** Separate from the provider so each file exports one kind of thing — see ThemeContext. */
export const AuthContext = createContext<AuthContextValue | null>(null);
